/**
 * Creates a ResizeObserver whose callback is debounced via requestAnimationFrame
 * to prevent "ResizeObserver loop completed with undelivered notifications" errors.
 *
 * These errors occur when a ResizeObserver callback synchronously triggers layout
 * changes that cause additional resize notifications the browser can't deliver in
 * the same animation frame. Deferring to RAF breaks this synchronous loop.
 */
export function createDebouncedResizeObserver(callback: ResizeObserverCallback): {
  observer: ResizeObserver;
  disconnect: () => void;
} {
  let rafId: number | null = null;
  let latestArgs: Parameters<ResizeObserverCallback>;
  const observer = new ResizeObserver((...args) => {
    latestArgs = args;
    if (rafId) return;
    rafId = requestAnimationFrame(() => {
      rafId = null;
      callback(...latestArgs);
    });
  });
  return {
    observer,
    disconnect: () => {
      if (rafId) cancelAnimationFrame(rafId);
      observer.disconnect();
    },
  };
}
